import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: `By accessing or using the Filter Hotels platform ("Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our Service. These Terms apply to all users, including travel agents, corporate clients, and any other business entities accessing the platform.`,
  },
  {
    title: '2. Description of Service',
    content: `Filter Hotels is a B2B hotel aggregation platform that allows registered business users to search, compare, and book hotel accommodations from multiple vendors. We aggregate pricing and availability data from third-party hotel vendors and display it on our platform. Filter Hotels acts as an intermediary and is not directly responsible for the hotel services provided by vendors.`,
  },
  {
    title: '3. User Registration & Eligibility',
    content: `To use our Service, you must register for an account and receive approval from our administrative team. You must be a legally registered business entity or an authorized representative thereof. You agree to provide accurate, current, and complete information during registration and to update such information as necessary. Accounts are subject to approval and may be rejected or revoked at our discretion.`,
  },
  {
    title: '4. Account Responsibilities',
    content: `You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account. Filter Hotels will not be liable for any loss or damage arising from your failure to comply with this security obligation. You may not share your account credentials with third parties.`,
  },
  {
    title: '5. Booking & Payment',
    content: `All bookings made through Filter Hotels are subject to the terms and conditions of the respective hotel vendors. Prices displayed are subject to availability and may change without notice. Payment must be made in accordance with the payment methods available on the platform, including online payments, manual bank transfers, or the token-based wallet system. Filter Hotels reserves the right to cancel bookings in cases of pricing errors or vendor unavailability.`,
  },
  {
    title: '6. Cancellation & Refund Policy',
    content: `Cancellation policies vary by hotel vendor and property. The applicable cancellation policy will be displayed at the time of booking. Refunds, if applicable, will be processed in accordance with the vendor's policy. Filter Hotels is not responsible for any cancellation fees or non-refundable charges imposed by vendors. Refunds may take 7–14 business days to process depending on the payment method.`,
  },
  {
    title: '7. Intellectual Property',
    content: `All content on the Filter Hotels platform, including but not limited to text, graphics, logos, icons, images, and software, is the property of Filter Hotels or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.`,
  },
  {
    title: '8. Limitation of Liability',
    content: `Filter Hotels shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability to you for any claims arising from these Terms shall not exceed the amount paid by you to Filter Hotels in the three months preceding the claim. We do not guarantee the accuracy, completeness, or reliability of vendor pricing data.`,
  },
  {
    title: '9. Modifications to Terms',
    content: `Filter Hotels reserves the right to modify these Terms at any time. We will notify registered users of significant changes via email or platform notification. Your continued use of the Service after such modifications constitutes your acceptance of the updated Terms. We recommend reviewing these Terms periodically.`,
  },
  {
    title: '10. Governing Law',
    content: `These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts located in India. If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.`,
  },
  {
    title: '11. Contact Information',
    content: `If you have any questions about these Terms and Conditions, please contact us at filterhotels@gmail.com or call us at +91 7020886313. Our business hours are Monday through Friday, 9:00 AM to 6:00 PM IST.`,
  },
];

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms & Conditions – Filter Hotels';
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-indigo-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-300 hover:text-white transition-colors mb-8 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm">FH</span>
            </div>
            <span className="text-white/80 font-medium">Filter Hotels</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Terms & Conditions</h1>
          <p className="text-blue-200 text-lg">
            Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12"
        >
          <p className="text-gray-600 leading-relaxed mb-10 text-base border-l-4 border-blue-500 pl-4 bg-blue-50 py-3 pr-4 rounded-r-lg">
            Please read these Terms and Conditions carefully before using the Filter Hotels platform. These terms govern your access to and use of our services.
          </p>

          <div className="space-y-10">
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h2>
                <p className="text-gray-600 leading-relaxed">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
